import React, { useEffect, useState } from "react";

import {
  addDays,
  addHours,
  addMonths,
  addWeeks,
  addYears,
  differenceInHours,
  differenceInMinutes,
  setMinutes,
  startOfDay,
} from "date-fns";
import { get, set } from "lodash";
import { useDispatch, useSelector } from "react-redux";
import { useSnackbar } from "notistack";

import {
  DitLessonType,
  LearnerLessonType,
  LessonStatus,
  TimeSlotInput,
  TimeSlotsAvailabilityInput,
} from "@hubcapp/types";
import {
  calendarActions,
  selectCalendarShowAddLessonModal,
  selectCalendarView,
  selectNewLessonDefaultDate,
} from "@hubcapp/state";
import { createFakeEvent } from "@ds-developer/frontend";
import { useAuth, useTealium } from "@hubcapp/context";
import { useFormValidation } from "@hubcapp/hooks";

import { AddLessonFormState, EventType } from "./types";
import { getCalenderEventsQueryInput } from "../../../helpers";
import { isLessonMinutesIncrementValid, timeRangeIsValid } from "./helpers";
import { useMutationCreateTimeOff, useQueryLearners } from "./hooks";
import AddLessonModal from "./AddLessonModal";
import useCancelPendingLessons from "@hubcapp/components/Calendar/components/Modals/AddLessonModal/hooks/useCancelPendingLessons";
import useConvertPendingLessons from "@hubcapp/components/Calendar/components/Modals/AddLessonModal/hooks/useConvertPendingLessons";
import useMutationCreateLesson from "./hooks/useMutationCreateLesson";
import useQueryGetTimeSlotsAvailability from "@hubcapp/components/Calendar/components/Modals/ViewLessonModal/hooks/useQueryGetTimeSlotsAvailability";

// TODO This component (and children) need to be refactored to use react-hook-form when @material-ui/pickers v4 is released. This will include a renderInput prop that will make it much easier to pass the ref down
export const schema = (eventType?: EventType) => ({
  learnerId: {
    presence:
      eventType !== EventType.Unavailable
        ? {
            allowEmpty: false,
            message: "^This field is required",
            fullMessages: false,
          }
        : false,
  },
  type: {
    presence: {
      allowEmpty: false,
      message: "^This field is required",
      fullMessages: false,
    },
  },
  startDateTime: {
    presence: {
      allowEmpty: false,
      message: "^This field is required",
      fullMessages: false,
    },
    datetime: {
      earliest: startOfDay(new Date()),
      message: "^Cannot be in the past",
    },
  },
  endDateTime: {
    presence: {
      allowEmpty: false,
      message: "^This field is required",
      fullMessages: false,
    },
    datetime: {
      earliest: startOfDay(new Date()),
      message: "^Cannot be in the past",
    },
  },
  test: {
    presence:
      eventType !== EventType.Unavailable
        ? {
            allowEmpty: false,
            message: "^This field is required",
            fullMessages: false,
          }
        : false,
  },
});

const defaultInitialFormState = {
  isValid: false,
  values: {
    type: EventType.PaidLesson,
  },
  touched: {},
  errors: {},
};

const StateProvider: React.FC<{
  initialFormState?: AddLessonFormState;
  selectedDate?: Date;
  refetchCalendarData?: () => void;
}> = ({
  initialFormState = defaultInitialFormState,
  selectedDate,
  refetchCalendarData,
}) => {
  const dispatch = useDispatch();
  const { trackEvent } = useTealium();
  const {
    instructor: { id },
  } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const open = useSelector(selectCalendarShowAddLessonModal);
  const view = useSelector(selectCalendarView);
  const [eventType, setEventType] = useState(
    get(initialFormState, "values.type"),
  );
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [showPreviewBookingModal, setShowPreviewBookingModal] = useState<
    boolean
  >(false);
  const [showSuccessBookingModal, setShowSuccessBookingModal] = useState<
    string
  >("");

  const [bookedLessonsCount, setBookedLessonsCount] = useState<number>(0);
  const [
    convertPendingLessons,
    { data: convertPendingLessonsData, loading: convertPendingLessonsLoading },
  ] = useConvertPendingLessons();

  const initialDate =
    useSelector(selectNewLessonDefaultDate) ||
    addHours(setMinutes(new Date(), 0), 1);

  const { formState, reset, handleChange } = useFormValidation<
    AddLessonFormState
  >({
    initialFormState: {
      ...set(initialFormState, "values.startDateTime", initialDate),
      ...set(initialFormState, "values.endDateTime", addHours(initialDate, 2)),
      ...set(initialFormState, "values.test", false),
      ...set(initialFormState, "values.travelTime", 0),
      ...set(initialFormState, "values.repeatingLesson", "NO_REPEAT"),
      ...set(initialFormState, "values.repeatingLessonFrequencyNumber", null),
    },
    schema: schema(eventType),
  });

  useEffect(() => {
    handleChange(
      createFakeEvent(
        "startDateTime",
        selectedDate ? selectedDate : initialDate,
      ),
    );

    handleChange(
      createFakeEvent(
        "endDateTime",
        selectedDate ? addHours(selectedDate, 2) : addHours(initialDate, 2),
      ),
    );
  }, [selectedDate]);

  const { data: learnersData, loading: learnersLoading } = useQueryLearners({
    instructorId: id,
  });

  const close = () => {
    setSubmitting(false);
    dispatch(
      calendarActions.calendarShowAddLessonModal({
        showAddLessonModal: false,
      }),
    );
    reset();
  };

  const mutationResultHandlers = {
    onCompleted: close,
    onError: ({ graphQLErrors, networkError }: any) => {
      setSubmitting(false);
      graphQLErrors.forEach((error: { message: unknown }) => {
        const { message } = (error?.message as unknown) as {
          message: string;
        };
        let snackMessage = message || "";

        if (snackMessage === "New event overlaps with an existing event") {
          snackMessage =
            "Unable to book lesson. Please ensure that this lesson doesn't overlap with an existing booking.";
        }

        enqueueSnackbar(snackMessage, { variant: "error", role: "alert" });
      });

      if (networkError) {
        throw networkError;
      }
    },
  };

  const [
    createTimeOff,
    { loading: createTimeOffLoading },
  ] = useMutationCreateTimeOff(mutationResultHandlers);

  const [
    getTimeSlotsAvailability,
    { loading: loadingTimeSlots, data: timeSlotsAvailability },
  ] = useQueryGetTimeSlotsAvailability();

  const addTimeHandler = (time: Date, k: number) => {
    switch (formState.values.repeatingLesson) {
      case "DAILY":
        return addDays(time, k);
      case "WEEKLY":
        return addWeeks(time, k);
      case "MONTHLY":
        return addMonths(time, k);
    }
  };

  const checkAvailability = () => {
    const sotsArray: TimeSlotInput[] = Array.from(
      { length: formState.values.repeatingLessonFrequencyNumber ?? 0 },
      (_, k) => ({
        startDateTime: addTimeHandler(
          formState.values.startDateTime ?? new Date(),
          k,
        ),
        endDateTime: addTimeHandler(
          formState.values.endDateTime ?? new Date(),
          k,
        ),
        travelTime: formState.values.travelTime ?? 0,
      }),
    );
    const input = ({
      instructorKey: id,
      learnerKey: formState.values.learnerId,
      timeSlots: sotsArray,
      ignoreTimeOff: true,
      ignoreWorkHours: true,
      ignoreCalendarItemIds: [],
      prebook: true,
    } as unknown) as TimeSlotsAvailabilityInput;

    getTimeSlotsAvailability({
      variables: {
        input,
      },
    });
  };

  const [
    createLesson,
    { loading: createLessonLoading },
  ] = useMutationCreateLesson(mutationResultHandlers);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);

    const {
      type,
      endDateTime,
      learnerId,
      startDateTime,
      repeat,
      notes,
      allDay,
      test,
      travelTime,
    } = formState.values;

    if (!type || !endDateTime || !startDateTime) {
      return;
    }

    trackEvent({
      eventCategory: "Calendar",
      eventAction: "Button Click",
      eventLabel: type == "PAID_LESSON" ? "Add Lesson" : "Add Unavailable slot",
    });

    if (
      type === EventType.Unavailable &&
      differenceInMinutes(endDateTime, startDateTime) < 15
    ) {
      setSubmitting(false);
      enqueueSnackbar("Unavailable slots must be at least 15 minutes", {
        variant: "error",
        role: "alert",
      });
      return;
    }

    if (
      type !== EventType.Unavailable &&
      differenceInHours(endDateTime, startDateTime) < 1
    ) {
      setSubmitting(false);
      enqueueSnackbar("Lesson must be at least 1 hour", {
        variant: "error",
        role: "alert",
      });
      return;
    }

    if (
      !allDay &&
      (!isLessonMinutesIncrementValid(startDateTime) ||
        !isLessonMinutesIncrementValid(endDateTime))
    ) {
      setSubmitting(false);
      enqueueSnackbar(
        "Time entered must be in 15 minute increments (eg 12.00, 12.15, 12.30, 12.45).",
        {
          variant: "error",
          role: "alert",
        },
      );
      return;
    }

    if (
      type !== EventType.Unavailable &&
      !timeRangeIsValid(startDateTime, endDateTime)
    ) {
      setSubmitting(false);
      enqueueSnackbar("Lesson must be at least one hour duration", {
        variant: "error",
        role: "alert",
      });
      return;
    }

    let repeatSchedule;
    if (repeat) {
      repeatSchedule = {
        frequency: repeat,
        startDate: startDateTime,
        endDate: addYears(endDateTime, 1),
      };
    }

    if (type === EventType.PaidLesson && learnerId) {
      if (formState.values.repeatingLesson === "NO_REPEAT") {
        const selectedLearner = learnersData?.instructor?.learners?.find(
          learner => learner.id === learnerId,
        );
        const isDitLesson =
          selectedLearner &&
          selectedLearner?.learnerLessonType === LearnerLessonType.Dit;

        createLesson({
          variables: {
            learnerId,
            instructorId: id,
            startDateTime,
            endDateTime,
            test,
            travelTime,
            status: LessonStatus.Active,
            ...(isDitLesson && { ditLessonType: DitLessonType.DitCar }),
            notes: "",
            ...getCalenderEventsQueryInput(initialDate, view),
          },
        });
      } else {
        checkAvailability();
        setShowPreviewBookingModal(true);
      }
    }

    if (type === EventType.Unavailable) {
      createTimeOff({
        variables: {
          startDateTime,
          endDateTime,
          instructorId: id,
          notes: notes || "",
          repeatSchedule: repeatSchedule,
          ...getCalenderEventsQueryInput(initialDate, view),
        },
      });
    }
  };

  const handleCancel = () => {
    setShowSuccessBookingModal("");
    setShowPreviewBookingModal(false);
    trackEvent({
      eventCategory: "Calendar",
      eventAction: "Button Click",
      eventLabel: "Cancel",
    });

    close();
  };

  const handleEventTypeChange = (value: EventType) => {
    trackEvent({
      eventCategory: "Calendar",
      eventAction: "Interaction",
      eventLabel: value === "UNAVAILABLE" ? "Time Off" : "Lesson",
    });

    if (value === "PAID_LESSON") {
      handleChange(
        createFakeEvent(
          "endDateTime",
          addHours(formState.values.startDateTime || initialDate, 2),
        ),
      );
    } else if (value === "UNAVAILABLE") {
      handleChange(
        createFakeEvent(
          "endDateTime",
          addHours(formState.values.startDateTime || initialDate, 1),
        ),
      );
    }

    setEventType(value);
  };

  const [
    cancelPendingLessonsHandler,
    { loading: cancelPendingLessonsLoading },
  ] = useCancelPendingLessons();

  const removeAllPendingLessonsHandler = async () => {
    const bookedLessonIds: string[] = [];
    timeSlotsAvailability?.timeSlotsAvailability?.forEach(ts => {
      if (ts.availability && ts.bookedPendingLessonId) {
        bookedLessonIds.push(ts.bookedPendingLessonId);
      }
    });
    if (bookedLessonIds?.length && bookedLessonIds?.length > 0) {
      await cancelPendingLessonsHandler({
        variables: {
          input: {
            ids: bookedLessonIds,
          },
        },
      });
    }
  };

  const backPreviewBookingModalHandler = async () => {
    await removeAllPendingLessonsHandler();

    setShowPreviewBookingModal(false);
    setSubmitting(false);
  };

  const handleCreatePreBookedLessons = () => {
    const { learnerId } = formState.values;

    if (!learnerId) return;

    const bookedTimeSlots = timeSlotsAvailability?.timeSlotsAvailability?.filter(
      timeSlot => timeSlot.availability === true,
    );

    const ids = (
      bookedTimeSlots?.map(ts => ts.bookedPendingLessonId) || []
    ).filter((id): id is string => typeof id === "string");
    convertPendingLessons({
      variables: {
        input: {
          ids,
        },
      },
    })
      .then(() => {
        setShowPreviewBookingModal(false);
        setShowSuccessBookingModal(formState.values.learnerId || "");
        refetchCalendarData && refetchCalendarData();
      })
      .catch(() => {
        setShowPreviewBookingModal(false);
        // onComplete();
      });
  };

  useEffect(() => {
    if (convertPendingLessonsData) {
      setBookedLessonsCount(
        convertPendingLessonsData.convertPendingLessons.length,
      );
    }
  }, [convertPendingLessonsData]);

  return (
    <AddLessonModal
      {...formState}
      open={open}
      learnersLoading={learnersLoading}
      learners={get(learnersData, "instructor.learners")}
      createEventLoading={createTimeOffLoading || createLessonLoading}
      handleSubmit={handleSubmit}
      handleChange={handleChange}
      handleCancel={handleCancel}
      submitting={submitting}
      handleEventTypeChange={handleEventTypeChange}
      showPreviewBookingModal={showPreviewBookingModal}
      timeSlotsAvailability={timeSlotsAvailability}
      loadingTimeSlots={loadingTimeSlots}
      cancelPendingLessonsLoading={cancelPendingLessonsLoading}
      backPreviewBookingModalHandler={backPreviewBookingModalHandler}
      handleCreatePreBookedLessons={handleCreatePreBookedLessons}
      showSuccessBookingModal={showSuccessBookingModal}
      lessonsNumber={bookedLessonsCount}
      convertPendingLessonsLoading={convertPendingLessonsLoading}
    />
  );
};

export default StateProvider;
